import { useEffect, useState } from "react";
import { crewOpsApi } from "@/api";
import {
  FlaskConical, Plus, Trash2, Play, AlertTriangle, CheckCircle,
  Info, ChevronRight, Users, Plane, Zap, X,
} from "lucide-react";

const EVENT_TYPES = [
  { value: "sick_call", label: "Sick Call", color: "text-red-400 bg-red-400/10 border-red-400/30" },
  { value: "aog", label: "Aircraft AOG", color: "text-orange-400 bg-orange-400/10 border-orange-400/30" },
  { value: "delay", label: "Flight Delay", color: "text-amber-400 bg-amber-400/10 border-amber-400/30" },
  { value: "training_insertion", label: "Training Insertion", color: "text-blue-400 bg-blue-400/10 border-blue-400/30" },
  { value: "manual_swap", label: "Manual Swap", color: "text-purple-400 bg-purple-400/10 border-purple-400/30" },
];

const RISK_COLORS = {
  critical: "text-red-400",
  high: "text-orange-400",
  medium: "text-amber-400",
  low: "text-green-400",
};
const RISK_BORDER = {
  critical: "border-l-red-500",
  high: "border-l-orange-500",
  medium: "border-l-amber-500",
  low: "border-l-green-500",
};
const PRIORITY_COLORS = {
  critical: "text-red-400",
  high: "text-orange-400",
  medium: "text-amber-400",
  low: "text-muted-foreground",
};

function statusBadge(status) {
  const map = {
    draft: "text-muted-foreground border-border",
    simulated: "text-amber-400 border-amber-400/40",
    committed: "text-green-400 border-green-400/40",
  };
  return `inline-flex px-2 py-0.5 rounded-sm text-xs font-bold border uppercase ${map[status] || "text-muted-foreground border-border"}`;
}

export default function ScenarioSimulator() {
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [crew, setCrew] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [newScenario, setNewScenario] = useState({ name: "", description: "" });
  const [newEvent, setNewEvent] = useState({ event_type: "sick_call", target_crew_id: "", target_pairing_id: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [addingEvent, setAddingEvent] = useState(false);

  const fetchScenarios = async () => {
    const r = await crewOpsApi.getScenarios();
    setScenarios(r.data);
    setLoading(false);
  };

  useEffect(() => {
    fetchScenarios();
    crewOpsApi.getCrew().then(r => setCrew(r.data));
    crewOpsApi.getPairings().then(r => setPairings(r.data));
  }, []);

  const loadScenario = async (id) => {
    const r = await crewOpsApi.getScenario(id);
    setActiveScenario(r.data);
  };

  const handleCreateScenario = async () => {
    if (!newScenario.name.trim()) return;
    setCreating(true);
    const r = await crewOpsApi.createScenario(newScenario);
    setCreating(false);
    setShowCreateForm(false);
    setNewScenario({ name: "", description: "" });
    await fetchScenarios();
    await loadScenario(r.data.id);
  };

  const handleAddEvent = async () => {
    if (!activeScenario || !newEvent.description.trim()) return;
    setAddingEvent(true);
    await crewOpsApi.addScenarioEvent(activeScenario.id, {
      ...newEvent,
      target_crew_id: newEvent.target_crew_id || null,
      target_pairing_id: newEvent.target_pairing_id || null,
    });
    setAddingEvent(false);
    setShowEventForm(false);
    setNewEvent({ event_type: "sick_call", target_crew_id: "", target_pairing_id: "", description: "" });
    await loadScenario(activeScenario.id);
    await fetchScenarios();
  };

  const handleSimulate = async () => {
    if (!activeScenario) return;
    setSimulating(true);
    await crewOpsApi.simulateScenario(activeScenario.id);
    setSimulating(false);
    await loadScenario(activeScenario.id);
    await fetchScenarios();
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    await crewOpsApi.deleteScenario(id);
    if (activeScenario?.id === id) setActiveScenario(null);
    await fetchScenarios();
  };

  const result = activeScenario?.simulation_result;

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="scenario-simulator">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Crew Rostering · Module E</div>
          <h1 className="text-2xl font-black font-heading text-foreground flex items-center gap-2">
            <FlaskConical size={20} className="text-primary" />
            What-If Scenario Simulator
          </h1>
          <p className="text-xs text-muted-foreground mt-1">Sandbox disruption events against the live roster — test impact before committing</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-sm hover:bg-primary/90 transition-colors"
          data-testid="create-scenario-btn"
        >
          <Plus size={13} />
          New Scenario
        </button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Total", scenarios.length, "text-primary"],
          ["Draft", scenarios.filter(s => s.status === "draft").length, "text-muted-foreground"],
          ["Simulated", scenarios.filter(s => s.status === "simulated").length, "text-amber-400"],
          ["Committed", scenarios.filter(s => s.status === "committed").length, "text-green-400"],
        ].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-4">
            <div className={`text-3xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{lbl}</div>
          </div>
        ))}
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-5 gap-4 items-start">

        {/* Left — scenario list */}
        <div className="col-span-2 space-y-2">
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Scenarios</div>

          {loading && <div className="text-xs text-muted-foreground py-4">Loading...</div>}

          {!loading && scenarios.length === 0 && (
            <div className="bg-card border border-border rounded-sm p-6 text-center">
              <FlaskConical size={24} className="text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-xs text-muted-foreground">No scenarios yet. Create one to start testing.</p>
            </div>
          )}

          {scenarios.map(s => (
            <div
              key={s.id}
              onClick={() => loadScenario(s.id)}
              className={`bg-card border rounded-sm p-3 cursor-pointer transition-colors group ${activeScenario?.id === s.id ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"}`}
              data-testid={`scenario-item-${s.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-foreground truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed line-clamp-2">{s.description}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={statusBadge(s.status)}>{s.status}</span>
                    <span className="text-xs text-muted-foreground font-mono">{s.event_count} event{s.event_count !== 1 ? "s" : ""}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <ChevronRight size={13} className={`text-muted-foreground ${activeScenario?.id === s.id ? "text-primary" : "group-hover:text-primary"}`} />
                  <button
                    onClick={e => handleDelete(s.id, e)}
                    className="p-1 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`delete-scenario-${s.id}`}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {/* Inline create form */}
          {showCreateForm && (
            <div className="bg-card border border-primary/30 rounded-sm p-4 space-y-3" data-testid="create-scenario-form">
              <div className="flex items-center justify-between">
                <div className="text-xs tracking-widest uppercase text-primary font-bold">New Scenario</div>
                <button onClick={() => setShowCreateForm(false)}><X size={13} className="text-muted-foreground" /></button>
              </div>
              <input
                value={newScenario.name}
                onChange={e => setNewScenario(p => ({ ...p, name: e.target.value }))}
                placeholder="Scenario name..."
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="scenario-name-input"
              />
              <textarea
                value={newScenario.description}
                onChange={e => setNewScenario(p => ({ ...p, description: e.target.value }))}
                placeholder="Brief description..."
                rows={2}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                data-testid="scenario-description-input"
              />
              <div className="flex gap-2">
                <button
                  onClick={handleCreateScenario}
                  disabled={creating || !newScenario.name.trim()}
                  className="flex-1 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-50"
                  data-testid="confirm-create-scenario"
                >
                  {creating ? "Creating..." : "Create Scenario"}
                </button>
                <button onClick={() => setShowCreateForm(false)} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:border-primary/30">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Right — active scenario detail */}
        <div className="col-span-3 space-y-4">
          {!activeScenario ? (
            <div className="bg-card border border-border rounded-sm p-16 flex flex-col items-center justify-center text-center">
              <FlaskConical size={36} className="text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Select a scenario to view details</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Or create a new scenario to start testing disruptions</p>
            </div>
          ) : (
            <>
              {/* Scenario header card */}
              <div className="bg-card border border-border rounded-sm p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-foreground font-heading">{activeScenario.name}</div>
                    <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{activeScenario.description}</div>
                  </div>
                  <span className={statusBadge(activeScenario.status)}>{activeScenario.status}</span>
                </div>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleSimulate}
                    disabled={simulating || !activeScenario.events?.length}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-50 transition-colors"
                    data-testid="run-simulation-btn"
                  >
                    <Play size={12} />
                    {simulating ? "Simulating..." : "Run Simulation"}
                  </button>
                  {activeScenario.status === "simulated" && (
                    <button
                      className="flex items-center gap-2 px-4 py-2 border border-green-500/40 text-green-400 text-xs font-semibold rounded-sm hover:bg-green-400/5 transition-colors"
                      data-testid="commit-to-live-btn"
                    >
                      <CheckCircle size={12} />
                      Commit to Live
                    </button>
                  )}
                </div>
              </div>

              {/* Events panel */}
              <div className="bg-card border border-border rounded-sm p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">
                    Scenario Events ({activeScenario.events?.length || 0})
                  </div>
                  <button
                    onClick={() => setShowEventForm(!showEventForm)}
                    className="flex items-center gap-1 text-xs text-primary hover:underline"
                    data-testid="add-event-btn"
                  >
                    <Plus size={11} /> Add Event
                  </button>
                </div>

                {/* Add event form */}
                {showEventForm && (
                  <div className="border border-primary/20 rounded-sm p-3 space-y-3 bg-primary/5" data-testid="add-event-form">
                    <div className="flex items-center justify-between">
                      <div className="text-xs tracking-widest uppercase text-primary/80 font-bold">New Disruption Event</div>
                      <button onClick={() => setShowEventForm(false)}><X size={13} className="text-muted-foreground" /></button>
                    </div>
                    <select
                      value={newEvent.event_type}
                      onChange={e => setNewEvent(p => ({ ...p, event_type: e.target.value }))}
                      className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="event-type-select"
                    >
                      {EVENT_TYPES.map(et => <option key={et.value} value={et.value}>{et.label}</option>)}
                    </select>
                    <select
                      value={newEvent.target_crew_id}
                      onChange={e => setNewEvent(p => ({ ...p, target_crew_id: e.target.value }))}
                      className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="event-crew-select"
                    >
                      <option value="">Select crew member (optional)</option>
                      {crew.map(c => <option key={c.id} value={c.id}>{c.name} ({c.rank})</option>)}
                    </select>
                    <select
                      value={newEvent.target_pairing_id}
                      onChange={e => setNewEvent(p => ({ ...p, target_pairing_id: e.target.value }))}
                      className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="event-pairing-select"
                    >
                      <option value="">Select pairing (optional)</option>
                      {pairings.map(p => <option key={p.id} value={p.id}>{p.pairing_code} ({p.start_date} → {p.end_date})</option>)}
                    </select>
                    <input
                      value={newEvent.description}
                      onChange={e => setNewEvent(p => ({ ...p, description: e.target.value }))}
                      placeholder="Event description (required)..."
                      className="w-full bg-background border border-border rounded-sm px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      data-testid="event-description-input"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddEvent}
                        disabled={addingEvent || !newEvent.description.trim()}
                        className="flex-1 py-2 bg-primary text-primary-foreground text-xs font-semibold rounded-sm hover:bg-primary/90 disabled:opacity-50"
                        data-testid="confirm-add-event"
                      >
                        {addingEvent ? "Adding..." : "Add Event"}
                      </button>
                      <button onClick={() => setShowEventForm(false)} className="px-3 py-2 border border-border text-xs text-muted-foreground rounded-sm hover:border-primary/30">
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {!activeScenario.events?.length && !showEventForm && (
                  <div className="py-4 text-center text-xs text-muted-foreground">
                    No events yet. Click "Add Event" to inject a disruption.
                  </div>
                )}

                {activeScenario.events?.map(ev => {
                  const et = EVENT_TYPES.find(t => t.value === ev.event_type) || { label: ev.event_type, color: "text-muted-foreground border-border bg-muted/10" };
                  return (
                    <div key={ev.id} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`event-item-${ev.id}`}>
                      <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold border shrink-0 ${et.color}`}>{et.label}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground">{ev.description}</div>
                        {(ev.target_crew_name || ev.target_pairing_code) && (
                          <div className="flex gap-3 mt-0.5 flex-wrap">
                            {ev.target_crew_name && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Users size={10} />{ev.target_crew_name}
                              </span>
                            )}
                            {ev.target_pairing_code && (
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <Plane size={10} />{ev.target_pairing_code}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Ready to simulate prompt */}
              {!result && !!activeScenario.events?.length && (
                <div className="bg-card border border-border rounded-sm p-8 flex flex-col items-center text-center">
                  <Zap size={24} className="text-primary mb-2" />
                  <p className="text-sm text-foreground font-medium">Ready to Simulate</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {activeScenario.events.length} event{activeScenario.events.length !== 1 ? "s" : ""} loaded. Click "Run Simulation" to analyse impact.
                  </p>
                </div>
              )}

              {/* Simulation results */}
              {result && (
                <div className="space-y-3" data-testid="simulation-results">
                  {/* Risk banner */}
                  <div className={`bg-card border border-border border-l-4 ${RISK_BORDER[result.overall_risk] || "border-l-border"} rounded-sm p-4`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Simulation Result</div>
                        <div className={`text-lg font-bold font-heading ${RISK_COLORS[result.overall_risk]}`}>{result.risk_label}</div>
                        <div className="text-xs text-muted-foreground mt-1">{result.summary}</div>
                      </div>
                      <div className="text-right">
                        <div className={`text-4xl font-mono font-medium ${RISK_COLORS[result.overall_risk]}`}>{result.risk_score}</div>
                        <div className="text-xs text-muted-foreground">risk score</div>
                      </div>
                    </div>
                  </div>

                  {/* Counts */}
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ["Violations", result.violations?.length, "text-red-400"],
                      ["Crew Gaps", result.crew_gaps?.length, "text-orange-400"],
                      ["Actions", result.recommendations?.length, "text-primary"],
                    ].map(([lbl, val, col]) => (
                      <div key={lbl} className="bg-card border border-border rounded-sm p-3 text-center">
                        <div className={`text-2xl font-mono font-medium ${col}`}>{val}</div>
                        <div className="text-xs text-muted-foreground mt-1">{lbl}</div>
                      </div>
                    ))}
                  </div>

                  {/* Violations */}
                  {result.violations?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4 space-y-2">
                      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Compliance Violations</div>
                      {result.violations.map((v, i) => (
                        <div
                          key={i}
                          className={`bg-background rounded-sm p-3 border-l-4 ${v.severity === "error" ? "border-l-red-500" : v.severity === "warning" ? "border-l-amber-500" : "border-l-blue-500"}`}
                          data-testid={`violation-${i}`}
                        >
                          <div className="flex items-start gap-2">
                            {v.severity === "info"
                              ? <Info size={12} className="text-blue-400 mt-0.5 shrink-0" />
                              : <AlertTriangle size={12} className={`mt-0.5 shrink-0 ${v.severity === "error" ? "text-red-400" : "text-amber-400"}`} />
                            }
                            <div>
                              <span className="font-mono text-xs font-bold text-muted-foreground">{v.rule_code} </span>
                              <span className="text-xs text-foreground">{v.description}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Crew gaps */}
                  {result.crew_gaps?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4 space-y-2">
                      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Crew Coverage Gaps</div>
                      {result.crew_gaps.map((gap, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0" data-testid={`crew-gap-${i}`}>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono font-bold text-foreground">{gap.pairing_code}</span>
                              <span className={`text-xs font-bold uppercase ${gap.gap_severity === "critical" ? "text-red-400" : "text-amber-400"}`}>{gap.gap_severity}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">{gap.required_rank} required</div>
                          </div>
                          <div className="text-right">
                            {gap.best_replacement ? (
                              <>
                                <div className="text-xs font-medium text-foreground">{gap.best_replacement}</div>
                                <div className={`text-xs font-mono ${gap.replacement_fatigue < 50 ? "text-green-400" : gap.replacement_fatigue < 70 ? "text-amber-400" : "text-red-400"}`}>
                                  fatigue: {gap.replacement_fatigue}
                                </div>
                              </>
                            ) : (
                              <div className="text-xs text-red-400">No replacement available</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Fatigue deltas */}
                  {result.fatigue_deltas?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4 space-y-2">
                      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Fatigue Impact on Replacements</div>
                      {result.fatigue_deltas.map((fd, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0" data-testid={`fatigue-delta-${i}`}>
                          <div className="text-xs font-medium text-foreground">{fd.crew_name}</div>
                          <div className="flex items-center gap-3 font-mono text-xs">
                            <span className="text-muted-foreground">{fd.original_score}</span>
                            <span className="text-muted-foreground">→</span>
                            <span className={fd.new_score < 50 ? "text-green-400" : fd.new_score < 70 ? "text-amber-400" : "text-red-400"}>{fd.new_score}</span>
                            <span className={fd.delta > 0 ? "text-red-400" : "text-green-400"}>{fd.delta > 0 ? `+${fd.delta}` : fd.delta}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Recommendations */}
                  {result.recommendations?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4 space-y-2">
                      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold">Recommended Actions</div>
                      {result.recommendations.map((rec, i) => (
                        <div key={i} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`recommendation-${i}`}>
                          <span className={`text-xs font-bold uppercase shrink-0 w-14 ${PRIORITY_COLORS[rec.priority]}`}>{rec.priority}</span>
                          <div className="flex-1">
                            <div className="text-xs font-medium text-foreground">{rec.action}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{rec.rationale}</div>
                            <div className={`text-xs font-mono mt-1 ${rec.compliance_status.includes("Non-Compliant") ? "text-red-400" : rec.compliance_status.includes("Caution") ? "text-amber-400" : rec.compliance_status.includes("Compliant") ? "text-green-400" : "text-muted-foreground"}`}>
                              {rec.compliance_status}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
